/**
 * Wallet display resolver — PURE decision logic for which wallet balance the
 * app DISPLAYS (owner decision 2026-07-13: "display").
 *
 * While a signed-in user is acting in an account (users/{uid}/account_membership
 * back-pointer resolves an accountId), every balance display must show the
 * wallet that actually PAYS — the account's wallet (owner-personal or workspace
 * pool, as resolved by billing in functions/src/lib/accountExpense.ts) — not
 * necessarily the signed-in member's personal wallet. The server maintains a
 * display mirror at accounts/{accountId}/wallet/displaySummary (balance fields
 * only + payer_uid/wallet_mode), written transactionally by every debit and
 * refreshed by the top-up paths; verified account members may read it
 * (firestore.rules accounts/{id}/wallet/** block).
 *
 * This module is deliberately free of Firebase imports so it can be unit-tested
 * in a plain Node environment (tests/hooks/walletDisplayResolver.test.ts). The
 * summaries flow through generically — the resolver never inspects balance
 * values, only identity/loading facts.
 *
 * DISPLAY-ONLY by contract: nothing here changes where money moves. Money
 * movement UI (top-up success detection, deposit/withdraw caps) must keep using
 * the PERSONAL summary, which WalletSummaryContext continues to expose.
 */

export type WalletDisplayScope = 'personal' | 'account';

export type AccountMirrorStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'error';

export interface AccountMirrorInput<TSummary> {
  /** Lifecycle of the accounts/{id}/wallet/displaySummary listener. */
  status: AccountMirrorStatus;
  /** Converted mirror summary — only meaningful when status === 'ready'. */
  summary: TSummary | null;
  /**
   * Whose PERSONAL wallet pays (owner-personal mode), or null for the shared
   * workspace pool. When this equals the signed-in uid, the user's own LIVE
   * wallet feed IS the paying wallet and beats the (debit-refreshed) mirror.
   */
  payerUid: string | null;
}

export interface WalletDisplayInput<TSummary> {
  /** Firebase-Auth-confirmed uid (null = signed out / anonymous). */
  confirmedUid: string | null;
  /** The personal wallet summary committed by the existing W-round machinery. */
  personalSummary: TSummary;
  /**
   * The personal wallet's EXPOSED loading state — i.e. already including the
   * W-round `summaryForUid` gating (loading until the committed summary belongs
   * to the current confirmed uid). Passed through untouched on every personal
   * path so those semantics cannot regress here.
   */
  personalIsLoading: boolean;
  /** Whether the account_membership back-pointer listener has fired at least once. */
  membershipLoaded: boolean;
  /** accountId from users/{uid}/account_membership/main (null = none). */
  membershipAccountId: string | null;
  /** Mirror listener state — null when no mirror subscription is active. */
  mirror: AccountMirrorInput<TSummary> | null;
}

export interface WalletDisplayResolution<TSummary> {
  /** Which wallet the displayed summary belongs to. */
  scope: WalletDisplayScope;
  /** The summary to display (personal summary on every personal/fallback path). */
  summary: TSummary;
  /** Exposed loading state for the displayed wallet. */
  isLoading: boolean;
  /** The acting account id — set ONLY when scope === 'account' (for labeling). */
  accountId: string | null;
}

/**
 * Decide which wallet to display. Fail-safe: every ambiguous/error path falls
 * back to the personal wallet with its own loading semantics (the pre-feature
 * behavior), and signed-out users are byte-for-byte unchanged.
 */
export function resolveDisplayedWallet<TSummary>(
  input: WalletDisplayInput<TSummary>,
): WalletDisplayResolution<TSummary> {
  const personal: WalletDisplayResolution<TSummary> = {
    scope: 'personal',
    summary: input.personalSummary,
    isLoading: input.personalIsLoading,
    accountId: null,
  };

  // Signed out: the settled signed-out default — unchanged.
  if (!input.confirmedUid) return personal;

  // Signed in but the account context isn't known yet: report loading so the
  // low-balance gate stays silent (loading is non-blocking by contract) instead
  // of flashing a verdict computed against a possibly-wrong wallet.
  if (!input.membershipLoaded) return { ...personal, isLoading: true };

  const accountId = (input.membershipAccountId || '').trim() || null;
  // No acting account → personal wallet, existing semantics.
  if (!accountId) return personal;

  const mirror = input.mirror;
  // Acting in an account but the mirror hasn't answered yet → loading.
  if (!mirror || mirror.status === 'idle' || mirror.status === 'loading') {
    return { ...personal, isLoading: true };
  }

  if (mirror.status === 'ready' && mirror.summary !== null) {
    // The signed-in user's OWN wallet is the paying wallet (owner-personal mode
    // and they are the owner): the live personal feed is strictly fresher than
    // the debit-refreshed mirror, and it's the same wallet — display personal.
    if (mirror.payerUid === input.confirmedUid) return personal;
    return {
      scope: 'account',
      summary: mirror.summary,
      isLoading: false,
      accountId,
    };
  }

  // Mirror missing (account never debited since the feature shipped — lazy
  // backfill pending) or unreadable (not a verified member): personal fallback.
  return personal;
}
